import React, { useState, useMemo } from "react";
import { toReportNumber } from './reportNumbers';
import { Icon } from "@iconify/react";
import ActionButton from "../../ActionButton";
import { useEffect } from "react";
import TicketViewer from "./TicketViewer";
import { apiFetch } from "../../../../utils/api";
import KPI from "./KPI";
import RankedKPI from "./RankedKPI";
import { tiedRankings } from './reportRankings';
import Table from "./Table";
import { buildOrdersReportRows, matchesOrderSearch } from './ordersReportRows';
import Chart from "./Chart";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const toYmdDayNumber = (ymd) => {
  const [y, m, d] = String(ymd || "")
    .split("-")
    .map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d))
    return null;
  return Math.floor(Date.UTC(y, m - 1, d) / 86400000);
};


const formatYmdDate = (ymd) => {
  if (!ymd) return "-";
  const [y, m, d] = String(ymd).slice(0, 10).split("-").map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d))
    return "-";
  if (m < 1 || m > 12) return "-";
  return `${MONTHS[m - 1]} ${d}, ${y}`;
};


const Modal = ({ children, onClose }) => (
  <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
    <div className="bg-white dark:bg-gray-900 p-6 rounded shadow-lg border border-gray-200 dark:border-gray-700 max-w-3xl w-full relative">
      <button
        type="button"
        className="absolute top-2 right-2 text-gray-600 dark:text-gray-300 hover:text-red-500"
        onClick={onClose}
        aria-label="Close ticket details"
        title="Close"
      >
        <Icon
          icon="mdi:close"
          className="h-5 w-5"
          aria-hidden="true"
        />
      </button>
      {children}
    </div>
  </div>
);

const OrdersChartsSection = ({
  isDark = false,
  filters = {},
  ordersByDay = [],
  ordersHeatmap = [],
  ordersByTable = [],
  ordersByServer = [],
  ordersSummary = { total_orders: 0 },
  ordersList = [],
  ordersItemsCount = [],
  ordersBumpMinutes = [],
}) => {
  const [search, setSearch] = useState("");
  const [ticketDetail, setTicketDetail] = useState(null);
  // #REVIEW: The Order Path Sankey is intentionally withheld from the release until expo resolution, multi-screen routes, void paths, and timing semantics are validated against representative client data.

  // Build item count map
  const itemCountsMap = {};
  (Array.isArray(ordersItemsCount) ? ordersItemsCount : []).forEach(
    ({ order_id, item_count, bumped_at }) => {
      itemCountsMap[order_id] = { item_count, bumped_at };
    },
  );

  // Build time performance map
  const bumpMinutesMap = {};

  (Array.isArray(ordersBumpMinutes) ? ordersBumpMinutes : []).forEach(
    ({ order_id, bump_minutes }) => {
      bumpMinutesMap[order_id] = toReportNumber(bump_minutes);
    },
  );

  // Average / median bump time
  const bumpValues = Object.values(bumpMinutesMap).filter(
    (n) => Number.isFinite(n) && n >= 0,
  );

  const avgBumpNum = bumpValues.length
    ? bumpValues.reduce((sum, n) => sum + n, 0) / bumpValues.length
    : null;

  const avgTicketBumpTime = avgBumpNum !== null ? avgBumpNum.toFixed(2) : "-";

  const medianBumpNum = (() => {
    if (!bumpValues.length) return null;

    const sorted = [...bumpValues].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);

    return sorted.length % 2 === 1
      ? sorted[mid]
      : (sorted[mid - 1] + sorted[mid]) / 2;
  })();

  const medianTicketBumpTime =
    medianBumpNum !== null ? medianBumpNum.toFixed(2) : "-";

  // Filter for search box
  const filteredOrders = (ordersList || []).filter(order => matchesOrderSearch(order, search));

  // Rows for Order Report Table
  const rows = buildOrdersReportRows({ ordersList: filteredOrders, ordersItemsCount, ordersBumpMinutes });

  // Columns for Order Report Table
  const columns = [
    { field: "order_id", headerName: "Order ID", width: 100 },
    { field: "received_atFormatted", headerName: "Received", width: 180 },
    { field: "bumped_atFormatted", headerName: "Bumped", width: 180 },
    { field: "ticket_timeFormatted", headerName: "Ticket Time", flex: 1 },
    {
      field: "bumpStatus",
      headerName: "Performance",
      width: 120,
      renderCell: (params) => {
        const status = params.value;
        const styles = {
          Normal:
            "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
          "Needs Review":
            "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
          Outlier:
            "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
          "Insufficient Data":
            "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
        };
        if (!status) return <span className="text-gray-400">-</span>;
        return (
          <span
            className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || ""}`}
          >
            {status}
          </span>
        );
      },
    },
    { field: "table_name", headerName: "Table", width: 90 },
    { field: "server_name", headerName: "Server", flex: 1 },
    { field: "member_name", headerName: "Member", flex: 1 },
    { field: "items", headerName: "Items", width: 80 },
    {
      field: "ticket",
      headerName: "Ticket",
      width: 100,
      renderCell: (params) => (
        <span
          className="text-blue-600 dark:text-blue-400 underline cursor-pointer"
          onClick={async () => {
            const order = params.row;
            try {
              const detailRes = await apiFetch(
                `/updates-reports/orders-ticket-detail/${order.order_id}`,
              );
              if (!detailRes.ok) {
                throw new Error(
                  `Ticket detail request failed with HTTP ${detailRes.status}`,
                );
              }
              const detail = await detailRes.json();
              setTicketDetail({
                ...detail,
                order_meta: {
                  order_id: order.order_id,
                  table_name: order.table_name,
                  server_name: order.server_name,
                  member_name: order.member_name,
                  received_at: order.received_at,
                  bumped_at: order.bumped_at,
                },
              });
            } catch (err) {
              console.error("Error loading ticket detail:", err);
            }
          }}
        >
          View Ticket
        </span>
      ),
      exportable: false, // column not included in the order table export
    },
  ];

  // Derived stats
  const totalItems = Object.values(itemCountsMap).reduce(
    (sum, entry) => sum + (Number(entry.item_count) || 0),
    0,
  );

  // Force it to be a number, even if the API sends a string
  const totalOrders = Number(ordersSummary?.total_orders || 0);

  const avgItemsPerOrder =
    totalOrders > 0 ? (totalItems / totalOrders).toFixed(1) : "-";

  const topOrdersDays = (() => {
    if (!Array.isArray(ordersByDay) || !ordersByDay.length) return [];

    const inSelectedRange = (row) => {
      const day = String(row?.date || "").slice(0, 10);
      if (!day) return false;
      if (filters?.startDate && day < String(filters.startDate)) return false;
      if (filters?.endDate && day > String(filters.endDate)) return false;
      return true;
    };

    const scopedDays =
      filters?.startDate || filters?.endDate
        ? ordersByDay.filter(inSelectedRange)
        : ordersByDay;

    return tiedRankings(scopedDays, 'total_orders');
  })();
  const topOrdersDayLabel = topOrdersDays.length > 1 ? `${topOrdersDays.length} dates` : formatYmdDate(topOrdersDays[0]?.date);
  const topOrdersDayCount = toReportNumber(topOrdersDays[0]?.total_orders);
  const topServers = tiedRankings(ordersByServer, 'total_orders');
  const topTables = tiedRankings(ordersByTable, 'total_orders');

  const avgOrdersPerDay = (() => {
    const total = Number(ordersSummary?.total_orders || 0);
    if (!total) return "-";
    const todayYmd = new Date().toISOString().slice(0, 10);
    const minDateStr =
      Array.isArray(ordersByDay) && ordersByDay.length
        ? ordersByDay
            .map((d) => String(d?.date || "").slice(0, 10))
            .filter(Boolean)
            .sort()[0]
        : null;
    const startYmd = filters?.startDate || minDateStr || null;
    const endYmd = filters?.endDate || todayYmd;
    const startDay = toYmdDayNumber(startYmd);
    const endDay = toYmdDayNumber(endYmd);
    if (!Number.isFinite(startDay) || !Number.isFinite(endDay)) return "-";
    const days = Math.max(1, endDay - startDay + 1);
    return (total / days).toFixed(2);
  })();

  /*  Calculating orders by hour for the bar chart */
  const ordersByHour = useMemo(() => {
    // Include every hour so hours without orders still appear.
    const hours = Array.from({ length: 24 }, (_, hour) => ({
      hour,
      total_orders: 0,
    }));

    // Sum the same backend-localized buckets used by the Overview heatmap.
    if (Array.isArray(ordersHeatmap) && ordersHeatmap.length) {
      for (const bucket of ordersHeatmap) {
        const hour = Number(bucket?.hour_of_day);
        const count = Number(bucket?.total_orders);
        if (Number.isInteger(hour) && hour >= 0 && hour <= 23 && Number.isFinite(count)) {
          hours[hour].total_orders += count;
        }
      }
      return hours;
    }

    for (const order of ordersList) {
      const receivedAt = String(order?.received_at || "");

      // Read the hour directly from the supplied reporting timestamp.
      const match = receivedAt.match(/^\d{4}-\d{2}-\d{2}[T ](\d{2}):/);

      if (!match) continue;

      const hour = Number(match[1]);

      if (hour < 0 || hour > 23) continue;

      hours[hour].total_orders += 1;
    }

    return hours;
  }, [ordersHeatmap, ordersList]);

  const [selectedServer, setSelectedServer] = useState("");
  const [selectedTable, setSelectedTable] = useState("");

  // Clear linked chart selections when the applied report data or filters change.
  useEffect(() => {
    setSelectedServer("");
    setSelectedTable("");
  }, [
    ordersList,
    filters.site_id,
    filters.location_id,
    filters.sublocation_id,
    filters.station,
    filters.daypart,
    filters.startDate,
    filters.endDate,
  ]);

  /* Count tickets by server, optionally restricted to the selected table. */
  const serverChartData = useMemo(() => {
    const counts = new Map();

    for (const order of ordersList) {
      const server = order.server_name || "Unknown";
      const table = order.table_name || "Unknown";

      if (selectedTable && table !== selectedTable) continue;

      counts.set(server, (counts.get(server) || 0) + 1);
    }

    return Array.from(counts, ([name, total_orders]) => ({
      name,
      total_orders,
    })).sort((a, b) => b.total_orders - a.total_orders);
  }, [ordersList, selectedTable]);

  // Count tickets by table, optionally restricted to the selected server.
  const tableChartData = useMemo(() => {
    const counts = new Map();

    for (const order of ordersList) {
      const server = order.server_name || "Unknown";
      const table = order.table_name || "Unknown";

      if (selectedServer && server !== selectedServer) continue;

      counts.set(table, (counts.get(table) || 0) + 1);
    }

    return Array.from(counts, ([name, total_orders]) => ({
      name,
      total_orders,
    })).sort((a, b) => b.total_orders - a.total_orders);
  }, [ordersList, selectedServer]);

  /* Combine ticket metadata, item quanities, and bump time by order ID */
  const ticketHealthData = useMemo(() => {
    const quantities = new Map(
      ordersItemsCount.map((row) => [
        String(row.order_id),
        Number(row.item_count),
      ]),
    );

    const durations = new Map(
      ordersBumpMinutes
        .filter((row) => row.bump_minutes != null)
        .map((row) => [String(row.order_id), Number(row.bump_minutes)]),
    );

    return ordersList.flatMap((order) => {
      const id = String(order.order_id);
      const quantity = quantities.get(id);
      const minutes = durations.get(id);

      // Missing duration is not a zero-minute completed ticket.
      if (
        !Number.isFinite(quantity) ||
        quantity < 0 ||
        !Number.isFinite(minutes) ||
        minutes < 0
      ) {
        return [];
      }

      return [
        {
          id,
          x: quantity,
          y: minutes,
          server: order.server_name || "Unknown",
          table: order.table_name || "Unknown",
        },
      ];
    });
  }, [ordersList, ordersItemsCount, ordersBumpMinutes]);

  // Group valid bump durations into fixed ten-minute buckets.
  const ticketTimeDistribution = useMemo(() => {
    const buckets = [
      { label: "0–10", min: 0, max: 10, tickets: 0 },
      { label: "10-20", min: 10, max: 20, tickets: 0 },
      { label: "20-30", min: 20, max: 30, tickets: 0 },
      { label: "30-40", min: 30, max: 40, tickets: 0 },
      { label: "40-50", min: 40, max: 50, tickets: 0 },
      { label: "50-60", min: 50, max: 60, tickets: 0 },
      { label: "60-70", min: 60, max: 70, tickets: 0 },
      { label: "70-80", min: 70, max: 80, tickets: 0 },
      { label: "80+", min: 80, max: Infinity, tickets: 0 },
    ];

    for (const ticket of ticketHealthData) {
      const bucket = buckets.find(
        (entry) => ticket.y >= entry.min && ticket.y < entry.max,
      );

      if (bucket) bucket.tickets += 1;
    }

    return buckets;
  }, [ticketHealthData]);

  // Component Definition & UI Layout
  return (
    <div className="reports-tab-content dark:text-gray-100 space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI
          title="Total Orders"
          value={ordersSummary.total_orders?.toLocaleString() ?? "-"}
          color="blue"
        />
        <KPI
          title="Avg. Items per Order"
          value={avgItemsPerOrder?.toLocaleString() ?? "-"}
          color="purple"
        />
        <RankedKPI
          title="Top Orders Day"
          label={topOrdersDayLabel}
          value={topOrdersDayCount}
          showTooltip={topOrdersDays.length > 1}
          tooltipContent={topOrdersDays.map((day, index) => <div key={index}>{formatYmdDate(day.date)}</div>)}
          color="green"
        />
        <RankedKPI
          title="Top Server"
          label={topServers.length > 1 ? `${topServers.length} servers` : topServers[0]?.server_name}
          value={toReportNumber(topServers[0]?.total_orders)}
          showTooltip={topServers.length > 1}
          tooltipContent={topServers.map((server, index) => <div key={index}>{server.server_name}</div>)}
          color="yellow"
        />
        <RankedKPI
          title="Top Table"
          label={topTables.length > 1 ? `${topTables.length} tables` : topTables[0]?.table_name}
          value={toReportNumber(topTables[0]?.total_orders)}
          showTooltip={topTables.length > 1}
          tooltipContent={topTables.map((table, index) => <div key={index}>{table.table_name}</div>)}
          color="indigo"
        />
        <KPI
          title="Avg. Orders per Day"
          value={avgOrdersPerDay.toLocaleString() ?? "-"}
          color="cyan"
        />
        <KPI
          title="Avg. Ticket Bump (mins)"
          value={avgTicketBumpTime.toLocaleString() ?? "-"}
          color="rose"
        />
        <KPI
          title="Median Ticket Bump (mins)"
          value={medianTicketBumpTime.toLocaleString() ?? "-"}
          color="emerald"
        />
      </div>

      {/* Order Viewer - table */}
      <Table
        title="Order Report"
        rows={rows}
        columns={columns}
        isDark={isDark}
        getRowId={(rows) => rows.order_id}
        searchable
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by Order ID, Server, Table..."
        exportable
        exportTitle="Orders Report"
        exportFileName="Orders Report"
        exportDateRange={filters}
      />

      {/* Charts */}
      <div className="reports-panel-stack">
        {/*Orders by Hour Chart - more in depth of the heatmap to really show what times are the busiest */}
        <Chart
          type="bar"
          title="Orders by Hour"
          data={ordersByHour}
          isDark={isDark}
          xAxis={[
            {
              dataKey: "hour",
              scaleType: "band",
              label: "Received hour",
              valueFormatter: (hour) => {
                const suffix = hour < 12 ? "AM" : "PM";
                const displayHour = hour % 12 || 12;

                return `${displayHour} ${suffix}`;
              },
            },
          ]}
          yAxis={[
            {
              label: "Orders",
              min: 0,
            },
          ]}
          series={[
            {
              dataKey: "total_orders",
              label: "Orders received",
            },
          ]}
        />

        {/*Orders by Server and Table - drillable can show the relationship of how many orders and tables server was responsible for, can show top servers as a whole, and represent total orders by the table*/}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Server: {selectedServer || "All"}
              {" · "}
              Table: {selectedTable || "All"}
            </p>

            <ActionButton
              type="button"
              onClick={() => {
                setSelectedServer("");
                setSelectedTable("");
              }}
            >
              Reset selection
            </ActionButton>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <Chart
              type="bar"
              title="Orders by Server"
              subtitle={
                selectedTable
                  ? `Servers handling ${selectedTable}`
                  : "Click a server to inspect its tables"
              }
              data={serverChartData}
              isDark={isDark}
              height={320}
              xAxis={[{ dataKey: "name", scaleType: "band" }]}
              yAxis={[{ label: "Orders", min: 0 }]}
              series={[{ dataKey: "total_orders", label: "Orders" }]}
              onItemClick={(_event, item) => {
                const row = serverChartData[item?.dataIndex];

                if (!row) return;

                setSelectedServer((current) =>
                  current === row.name ? "" : row.name,
                );
              }}
            />

            <Chart
              type="bar"
              title="Orders by Table"
              subtitle={
                selectedServer
                  ? `Tables handled by ${selectedServer}`
                  : "Click a table to inspect its servers"
              }
              data={tableChartData}
              isDark={isDark}
              height={320}
              xAxis={[{ dataKey: "name", scaleType: "band" }]}
              yAxis={[{ label: "Orders", min: 0 }]}
              series={[{ dataKey: "total_orders", label: "Orders" }]}
              onItemClick={(_event, item) => {
                const row = tableChartData[item?.dataIndex];

                if (!row) return;

                setSelectedTable((current) =>
                  current === row.name ? "" : row.name,
                );
              }}
            />
          </div>
        </div>

        {/* #REVIEW: Restore the Order Path Sankey only after its route widths, completion rules, multi-expo handling, and elapsed-time labels pass product testing. */}
        <Chart
          type="scatter"
          title="Ticket Health Scatter"
          subtitle="One point per ticket; duration averages receipt-to-bump times across selected screens, not final expo completion"
          isDark={isDark}
          xAxis={[
            {
              label: "Item quantity",
              min: 0,
            },
          ]}
          yAxis={[
            {
              label: "Bump duration (minutes)",
              min: 0,
            },
          ]}
          series={[
            {
              label: "Ticket",
              data: ticketHealthData,
              valueFormatter: (point) =>
                point
                  ? `Order ${point.id}: ${point.x} items, ${point.y.toFixed(1)} min`
                  : "",
            },
          ]}
        />
        <Chart
          type="bar"
          title="Ticket Time Distribution"
          subtitle="How many tickets are being bumped within a 10-minute range histogram"
          data={ticketTimeDistribution}
          isDark={isDark}
          height={320}
          xAxis={[
            {
              dataKey: "label",
              scaleType: "band",
              label: "Bump Duration (minutes)",
            },
          ]}
          yAxis={[
            {
              label: "Tickets",
              min: 0,
            },
          ]}
          series={[
            {
              dataKey: "tickets",
              label: "Tickets",
            },
          ]}
        />
      </div>

      {/* Ticket Modal */}
      {ticketDetail && (
        <Modal onClose={() => setTicketDetail(null)}>
          <div className="space-y-4 max-h-[80vh] overflow-y-auto">
            <h4 className="text-blue-700 dark:text-blue-300 font-semibold">
              Ticket Detail: Order #
              {ticketDetail?.order_id || ticketDetail?.order_meta?.order_id}
            </h4>
            <TicketViewer ticketDetail={ticketDetail} />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default OrdersChartsSection;
